<script lang="ts">
  import { aiFeaturesStore } from '$lib/stores/aiFeatures'
  import type { Segment } from '$lib/stores/aiFeatures'
  import { settingsStore, settingsActions } from '$lib/stores/settings'
  import { AI_MODELS } from '$lib/config/aiModels.config'
  import SegmentProcessorController from '../controllers/SegmentProcessorController.svelte'
  import PanelSynchronizerController from '../controllers/PanelSynchronizerController.svelte'
  import SegmentEditorController from '../controllers/SegmentEditorController.svelte'
  import SplitResizeController from '../controllers/SplitResizeController.svelte'
  import DiffRendererController from '../controllers/DiffRendererController.svelte'
  import { Check, X, SkipForward } from 'lucide-svelte'

  interface Props {
    originalText: string
    mode: 'paraphrase' | 'expand' | 'shrink'
    segments?: Segment[]
    isProcessing?: boolean
    isIndividualUpdate?: boolean
    isBulkProcessing?: boolean  // Flag to prevent SegmentProcessorController interference
    yamlContent?: string  // Optional YAML front matter
    onSegmentHover?: (segmentId: string | null) => void
    onSegmentsChange?: (segments: Segment[]) => void
    onSplitEditorApply?: (newText: string) => void
    onSplitEditorCancel?: () => void
    onPrompt?: (segmentId?: string) => void
    onBulkPrompt?: (prompt: string, modelId: string) => void
  }

  let { 
    originalText, 
    mode = 'paraphrase', 
    segments: propsSegments, 
    isProcessing: propsIsProcessing = false, 
    isIndividualUpdate = $bindable(false),
    isBulkProcessing = false,
    yamlContent,
    onSegmentHover, 
    onSegmentsChange,
    onSplitEditorApply,
    onSplitEditorCancel,
    onPrompt,
    onBulkPrompt
  }: Props = $props()

  // State management
  let splitRatio = $state(50)
  let isDragging = $state(false)
  let segments = $state<Segment[]>(propsSegments || [])
  let isProcessing = $state(propsIsProcessing)
  let showDiffOriginal = $state(true)
  let showDiffSuggested = $state(true)
  let editingSegmentId = $state<string | null>(null)
  let hoveredSegmentId = $state<string | null>(null)
  let showYaml = $state(false)  // YAML collapsed by default
  
  // Bulk prompt state for split editor
  let showSplitBulkPrompt = $state(false)
  let splitPromptInput = $state('')
  let splitPromptTextareaRef = $state<HTMLTextAreaElement | null>(null)
  let splitGenerationBarRef = $state<HTMLDivElement | null>(null)
  let isSplitPromptProcessing = $state(false)
  let showModelDropdown = $state(false)
  let modelSelectorRef = $state<HTMLDivElement | null>(null)
  
  // Sync segments from props - ensure deep reactivity
  $effect(() => {
    if (propsSegments) {
      segments = [...propsSegments]
    }
  })
  
  // Controller references
  let segmentProcessor: SegmentProcessorController
  let panelSynchronizer: PanelSynchronizerController
  let segmentEditor: SegmentEditorController
  let splitResizer: SplitResizeController
  let diffRendererController: DiffRendererController
  
  // Bind character counts from DiffRendererController
  let controllerOriginalCharCount = $state(0)
  let controllerSuggestedCharCount = $state(0)

  // Initialize segments through controller
  $effect(() => {
    if (segmentProcessor && !isIndividualUpdate && !isBulkProcessing) {
      // Only reset and initialize when NOT doing individual updates OR bulk processing
      // This prevents bulk re-processing when updating single segments or during bulk actions
      segmentProcessor.resetProcessingState()
      segmentProcessor.initializeSegments(propsSegments)
    }
  })

  // Update processing state from props
  $effect(() => {
    isProcessing = propsIsProcessing
  })

  // Delegate processing to controller
  async function processSegments(segs: Segment[]) {
    if (segmentProcessor) {
      await segmentProcessor.processSegments(segs)
    }
  }

  // Delegate resize to controller
  function startResize(e: MouseEvent) {
    if (splitResizer) {
      splitResizer.startResize(e)
    }
  }

  // Delegate editing to controller
  function startEditing(segmentId: string, currentText: string) {
    if (segmentEditor) {
      segmentEditor.startEditing(segmentId, currentText)
    }
  }
  
  function saveEditing(segmentId: string, newText: string) {
    if (segmentEditor) {
      segmentEditor.saveEditing(segmentId, newText)
    }
  }
  
  function handleKeydown(e: KeyboardEvent, segmentId: string) {
    if (segmentEditor) {
      segmentEditor.handleKeydown(e, segmentId)
    }
  }

  // Focus on mount action for editable elements
  function focusOnMount(node: HTMLElement) {
    if (segmentEditor) {
      segmentEditor.focusOnMount(node)
    }
  }

  // Get diff data from controller - ensure controller is initialized
  let segmentDiffs = $derived(diffRendererController?.getSegmentDiffs() ?? [])
  
  
  // Use bound character counts
  let originalCharCount = $derived(controllerOriginalCharCount)
  let suggestedCharCount = $derived(controllerSuggestedCharCount)
  let originalStats = $derived(diffRendererController?.getOriginalStats() ?? null)
  let suggestedStats = $derived(diffRendererController?.getSuggestedStats() ?? null)
  
  // Get selected AI model (use global selection, default to OpenRouter GPT-OSS)
  let selectedModel = $derived(settingsStore.get().ai?.selectedModel || 'openai/gpt-oss-120b')
  let currentModelId = $derived(selectedModel)
  
  // Get all available AI models from configuration
  let allAIModels = $derived.by(() => {
    const models = []
    const providers = settingsStore.get().aiProviders || []
    
    // Get enabled providers
    const enabledProviders = providers.filter(p => p.enabled)
    
    // Add models from each enabled provider
    for (const provider of enabledProviders) {
      const providerModels = AI_MODELS[provider.id] || []
      models.push(...providerModels)
    }
    
    // Always include OpenRouter models (free)
    const openrouterModels = AI_MODELS['openrouter'] || []
    models.push(...openrouterModels)
    
    return models
  })
  
  let currentAIModel = $derived(
    allAIModels.find(m => m.id === currentModelId) || 
    allAIModels.find(m => m.id === 'openai/gpt-oss-120b') || // Default to OpenRouter GPT-OSS
    null
  )
  
  // Handle split prompt button click
  function handleSplitPromptClick() {
    if (showSplitBulkPrompt) {
      showSplitBulkPrompt = false
      splitPromptInput = ''
    } else {
      showSplitBulkPrompt = true
      // Don't call onPrompt to avoid showing editor bottom prompt
    }
  }
  
  // Auto-focus textarea when prompt input is shown
  $effect(() => {
    if (showSplitBulkPrompt && splitPromptTextareaRef) {
      setTimeout(() => {
        splitPromptTextareaRef?.focus()
      }, 10)
    }
  })
  
  // Handle click outside to close prompt input
  $effect(() => {
    if (!showSplitBulkPrompt) return
    
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement
      if (splitGenerationBarRef && !splitGenerationBarRef.contains(target)) {
        const promptButton = target.closest('.prompt-toggle')
        if (!promptButton) {
          showSplitBulkPrompt = false
          splitPromptInput = ''
        }
      }
    }
    
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 10)
    
    return () => {
      clearTimeout(timer)
      document.removeEventListener('click', handleClickOutside)
    }
  })
  
  // Handle bulk prompt submission
  async function handleSplitPromptSubmit() {
    if (!splitPromptInput.trim() || isSplitPromptProcessing) return
    
    const modelId = currentModelId
    if (!modelId) return
    
    isSplitPromptProcessing = true
    const savedPrompt = splitPromptInput.trim()
    splitPromptInput = ''
    showSplitBulkPrompt = false
    
    try {
      // Call the onBulkPrompt handler with prompt and model
      if (onBulkPrompt) {
        await onBulkPrompt(savedPrompt, modelId)
      }
    } catch (error) {
      splitPromptInput = savedPrompt
      console.error('Failed to generate:', error)
    } finally {
      isSplitPromptProcessing = false
    }
  }
  
  // Auto-resize textarea based on content
  function autoResizeTextarea(e: Event) {
    const textarea = e.target as HTMLTextAreaElement
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }
  
  // Handle prompt input with max length
  function handlePromptInput(e: Event) {
    const textarea = e.target as HTMLTextAreaElement
    if (textarea.value.length > 200) {
      splitPromptInput = textarea.value.substring(0, 200)
    }
    autoResizeTextarea(e)
  }
  
  // Handle keyboard events
  function handleKeyDown(e: KeyboardEvent) {
    if (e.metaKey || e.ctrlKey) {
      if (['c', 'v', 'x', 'a', 'z'].includes(e.key.toLowerCase())) {
        return
      }
    }
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSplitPromptSubmit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      showSplitBulkPrompt = false
      splitPromptInput = ''
    }
  }
  
  // Sort AI models
  function sortedAIModels() {
    return [...allAIModels].sort((a, b) => {
      if (a.recommended && !b.recommended) return -1
      if (!a.recommended && b.recommended) return 1
      return a.name.localeCompare(b.name)
    })
  }
  
  // Select AI model
  function selectAIModel(model: any) {
    settingsActions.updateAI({ selectedModel: model.id })
    showModelDropdown = false
  }
  
  // Toggle model dropdown
  function toggleModelDropdown(e: Event) {
    e.stopPropagation()
    showModelDropdown = !showModelDropdown
  }
  
  // Close dropdown on outside click
  $effect(() => {
    if (showModelDropdown) {
      const handleClickOutside = (e: MouseEvent) => {
        if (modelSelectorRef && !modelSelectorRef.contains(e.target as Node)) {
          showModelDropdown = false
        }
      }
      
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  })
  
</script>

<!-- Controllers -->
<SegmentProcessorController
  bind:this={segmentProcessor}
  {originalText}
  {mode}
  bind:segments
  bind:isProcessing
  {onSegmentsChange}
/>

<PanelSynchronizerController
  bind:this={panelSynchronizer}
  {segments}
  bind:hoveredSegmentId
  {onSegmentHover}
/>

<SegmentEditorController
  bind:this={segmentEditor}
  bind:segments
  {isProcessing}
  bind:editingSegmentId
  {onSegmentsChange}
/>

<SplitResizeController
  bind:this={splitResizer}
  bind:splitRatio
  bind:isDragging
/>

<DiffRendererController
  bind:this={diffRendererController}
  segments={segments}
  bind:showDiffOriginal
  bind:showDiffSuggested
  bind:originalCharCount={controllerOriginalCharCount}
  bind:suggestedCharCount={controllerSuggestedCharCount}
/>

<div class="split-editor ultra-thin" class:dragging={isDragging}>
  <!-- YAML Header Section if present -->
  {#if yamlContent}
    <div class="yaml-section">
      <button 
        class="yaml-toggle"
        onclick={() => showYaml = !showYaml}
        aria-expanded={showYaml}
        aria-label={showYaml ? 'YAML 헤더 접기' : 'YAML 헤더 펼치기'}
      >
        <svg 
          class="yaml-toggle-icon" 
          class:expanded={showYaml}
          width="12" 
          height="12" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          stroke-width="2"
        >
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
        <span class="yaml-label">YAML Front Matter</span>
      </button>
      {#if showYaml}
        <div class="yaml-content">
          <pre>{yamlContent}</pre>
        </div>
      {/if}
    </div>
  {/if}
  
  <!-- Panels Only - No Header -->
  <div class="panels-container">
    <div class="panel original" style="width: {splitRatio}%">
      <div class="panel-label">
        <span>원본</span>
      </div>
      {#if segments.some(s => s.hasChanges && s.status !== 'applied')}
      <button 
        class="diff-toggle"
        onclick={() => showDiffOriginal = !showDiffOriginal}
        title={showDiffOriginal ? 'diff 숨기기' : 'diff 표시'}
      >
        {#if showDiffOriginal}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        {:else}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
            <line x1="1" y1="1" x2="23" y2="23"></line>
          </svg>
        {/if}
      </button>
      {/if}
      <div class="panel-content">
        {#each segments as segment, i}
          <div 
            class="segment-row" 
            data-index={i} 
            data-segment-id={segment.id}
            class:hovered={hoveredSegmentId === segment.id}
            class:applied={segment.status === 'applied'}
            class:skipped={segment.status === 'skipped'}
            role="region"
            aria-label={`Segment ${i + 1}`}
            onmouseenter={() => {
              onSegmentHover?.(segment.id)
              window.dispatchEvent(new CustomEvent('segment-hover', { detail: { segmentId: segment.id } }))
            }}
            onmouseleave={() => {
              onSegmentHover?.(null)
              window.dispatchEvent(new CustomEvent('segment-hover-end'))
            }}
          >
            <div class="segment-box" class:has-changes={segment.hasChanges}>
              <span class="segment-number">{i + 1}</span>
              <div class="segment-content">
                {#if segment.status === 'applied'}
                  <div class="text-content">{segment.original}</div>
                {:else if segment.status === 'skipped'}
                  <div class="text-content">{segment.original}</div>
                {:else if segment.hasChanges && segmentDiffs[i] && showDiffOriginal}
                  {#key `${segment.id}-${segment.suggested}`}
                  <div class="diff-content">
                    {@html segmentDiffs[i].left}
                  </div>
                  {/key}
                {:else}
                  <div class="text-content">{segment.original}</div>
                {/if}
              </div>
            </div>
          </div>
        {/each}
      </div>
      <div class="panel-footer">
        <span class="stat-value">
          {originalCharCount.toLocaleString()} 자
          {#if originalStats?.koreanChars}
            <span class="korean-count">({originalStats.koreanChars.toLocaleString()} 한글)</span>
          {/if}
        </span>
      </div>
    </div>
    
    <div 
      class="splitter"
      onmousedown={startResize}
      onkeydown={(e) => {
        if (e.key === 'ArrowLeft') {
          splitRatio = Math.max(20, splitRatio - 5)
        } else if (e.key === 'ArrowRight') {
          splitRatio = Math.min(80, splitRatio + 5)
        }
      }}
      role="slider"
      aria-label="패널 크기 조절"
      tabindex="0"
      aria-orientation="horizontal"
      aria-valuemin="20"
      aria-valuemax="80"
      aria-valuenow={splitRatio}
    >
      <div class="splitter-handle"></div>
    </div>
    
    <div class="panel suggested" style="width: {100 - splitRatio}%">
      <div class="panel-label">
        <span>AI</span>
      </div>
      {#if segments.some(s => s.hasChanges && s.status !== 'applied')}
      <button 
        class="diff-toggle"
        onclick={() => showDiffSuggested = !showDiffSuggested}
        title={showDiffSuggested ? 'diff 숨기기' : 'diff 표시'}
      >
        {#if showDiffSuggested}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        {:else}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
            <line x1="1" y1="1" x2="23" y2="23"></line>
          </svg>
        {/if}
      </button>
      {/if}
      <div class="panel-content">
        {#each segments as segment, i}
          <div 
            class="segment-row" 
            data-index={i} 
            data-segment-id={segment.id}
            class:hovered={hoveredSegmentId === segment.id}
            class:applied={segment.status === 'applied'}
            class:skipped={segment.status === 'skipped'}
            role="region"
            aria-label={`Segment ${i + 1}`}
            onmouseenter={() => {
              onSegmentHover?.(segment.id)
              window.dispatchEvent(new CustomEvent('segment-hover', { detail: { segmentId: segment.id } }))
            }}
            onmouseleave={() => {
              onSegmentHover?.(null)
              window.dispatchEvent(new CustomEvent('segment-hover-end'))
            }}
          >
            <div class="segment-box" class:has-changes={segment.hasChanges} class:is-processing={segment.isProcessing}>
              <span class="segment-number">{i + 1}</span>
              <div class="segment-content">
                {#if segment.isProcessing && !segment.suggested}
                  <!-- Show skeleton only when there's no suggested text yet -->
                  <div class="skeleton-text">
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line short"></div>
                    <div class="processing-label">AI 처리 중...</div>
                  </div>
                {:else if segment.isProcessing && segment.suggested}
                  <!-- Show streaming text with indicator -->
                  <button 
                    class="text-content clickable streaming"
                    onclick={() => startEditing(segment.id, segment.suggested || segment.original)}
                    type="button"
                    aria-label="Streaming segment"
                  >
                    {segment.suggested}
                    <span class="streaming-indicator">...</span>
                  </button>
                {:else if isProcessing && !segment.suggested && !segment.isProcessing}
                  <!-- Show skeleton for segments waiting to be processed -->
                  <div class="skeleton-text">
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line short"></div>
                  </div>
                {:else if editingSegmentId === segment.id}
                  <!-- Editing mode -->
                  <div 
                    class="text-content editable"
                    contenteditable="true"
                    role="textbox"
                    aria-label="Edit segment text"
                    tabindex="0"
                    spellcheck="false"
                    data-editing-id={segment.id}
                    onkeydown={(e) => handleKeydown(e, segment.id)}
                    onfocus={(e) => {
                      // Don't select all text, just place cursor
                      e.currentTarget.focus()
                    }}
                    onblur={(e) => {
                      const newText = e.currentTarget.innerText.trim()
                      saveEditing(segment.id, newText)
                    }}
                    oninput={(e) => {
                      // Prevent unwanted formatting
                      const target = e.currentTarget
                      if (target.innerHTML !== target.innerText) {
                        const pos = window.getSelection()?.getRangeAt(0).startOffset || 0
                        target.innerHTML = target.innerText
                        // Restore cursor position
                        const range = document.createRange()
                        const sel = window.getSelection()
                        range.setStart(target.childNodes[0] || target, Math.min(pos, target.innerText.length))
                        range.collapse(true)
                        sel?.removeAllRanges()
                        sel?.addRange(range)
                      }
                    }}
                    use:focusOnMount
                  >
                    {segment.suggested || segment.original}
                  </div>
                {:else if segment.status === 'applied'}
                  <button 
                    class="text-content clickable"
                    onclick={() => startEditing(segment.id, segment.suggested || segment.original)}
                    type="button"
                    aria-label="Edit applied segment"
                  >
                    {segment.suggested || segment.original}
                  </button>
                {:else if segment.status === 'skipped'}
                  <button 
                    class="text-content clickable"
                    onclick={() => startEditing(segment.id, segment.suggested || segment.original)}
                    type="button"
                    aria-label="Edit skipped segment"
                  >
                    {segment.suggested || segment.original}
                  </button>
                {:else if segment.hasChanges && segmentDiffs[i] && showDiffSuggested}
                  {#key `${segment.id}-${segment.suggested}`}
                  <button 
                    class="diff-content clickable"
                    title="클릭하여 편집"
                    onclick={() => startEditing(segment.id, segment.suggested || segment.original)}
                    type="button"
                    aria-label="Edit suggested change"
                  >
                    {@html segmentDiffs[i].right}
                  </button>
                  {/key}
                {:else}
                  <button 
                    class="text-content clickable"
                    onclick={() => startEditing(segment.id, segment.suggested || segment.original)}
                    type="button"
                    aria-label="Edit segment"
                  >
                    {segment.suggested || segment.original}
                  </button>
                {/if}
              </div>
            </div>
          </div>
        {/each}
      </div>
      <div class="panel-footer">
        <span class="stat-value">
          {suggestedCharCount.toLocaleString()} 자
          {#if suggestedStats?.koreanChars}
            <span class="korean-count">({suggestedStats.koreanChars.toLocaleString()} 한글)</span>
          {/if}
        </span>
      </div>
    </div>
  </div>
  
  <!-- Bulk Prompt Input Section (shown when prompt button is clicked) -->
  {#if showSplitBulkPrompt && onBulkPrompt}
    <div class="split-bulk-prompt-bar" bind:this={splitGenerationBarRef}>
      <div class="split-chat-container">
        {#if isSplitPromptProcessing}
          <!-- Loading state -->
          <div class="split-processing-state">
            <div class="split-spinner"></div>
            <span class="split-processing-text">AI가 처리 중...</span>
          </div>
        {:else}
          <!-- Main textarea -->
          <textarea
            bind:this={splitPromptTextareaRef}
            bind:value={splitPromptInput}
            class="split-prompt-input"
            placeholder="전체 텍스트를 어떻게 변경할까요?"
            maxlength="200"
            oninput={handlePromptInput}
            onkeydown={handleKeyDown}
            disabled={isSplitPromptProcessing}
            rows="1"
          ></textarea>
        {/if}
        
        <!-- Bottom controls -->
        <div class="split-bottom-controls">
          <!-- Model selector at bottom left -->
          <div class="split-model-selector" bind:this={modelSelectorRef}>
            <button
              class="split-model-btn"
              onclick={(e) => toggleModelDropdown(e)}
              title="AI 모델 선택"
              disabled={isSplitPromptProcessing}
            >
              {#if currentAIModel}
                <span class="split-model-name">{currentAIModel.name}</span>
              {:else}
                <span class="split-model-name">AI 모델</span>
              {/if}
              <svg class="split-chevron-icon" class:rotated={showModelDropdown} width="5" height="5" viewBox="0 0 24 24">
                <path d="M19 9l-7 7-7-7" stroke="currentColor" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            
            <!-- Model dropdown -->
            {#if showModelDropdown}
              <div class="split-model-dropdown">
                <div class="split-model-list">
                  {#each sortedAIModels() as model}
                    <button
                      class="split-model-item"
                      class:selected={currentModelId === model.id}
                      onclick={() => selectAIModel(model)}
                    >
                      <div class="split-item-info">
                        <div class="split-item-name">
                          {model.name}
                          {#if model.recommended}
                            <span class="split-recommended-badge">추천</span>
                          {/if}
                        </div>
                      </div>
                      {#if currentModelId === model.id}
                        <svg class="split-check-icon" width="10" height="10" viewBox="0 0 14 14">
                          <path 
                            d="M2 7l3 3 7-7" 
                            stroke="#6366f1" 
                            fill="none" 
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      {/if}
                    </button>
                  {/each}
                </div>
              </div>
            {/if}
          </div>
          
          <!-- Right side controls -->
          <div class="split-right-controls">
            <!-- Character count -->
            {#if splitPromptInput.length >= 50}
              <span class="split-char-count">{splitPromptInput.length}/200</span>
            {/if}
            
            <!-- Send button -->
            <button
              class="split-send-btn"
              onclick={handleSplitPromptSubmit}
              disabled={!splitPromptInput.trim()}
              title="생성 (Enter)"
              aria-label="생성"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  /* Use app's global color palette */
  .split-editor {
    --diff-add: var(--success, #16a34a);
    --diff-add-bg: #f0fdf4;
    --diff-del: var(--error, #dc2626);
    --diff-del-bg: #fef2f2;
    --diff-equal: var(--text-secondary, #6e6e6e);
    --panel-bg: var(--bg-primary, #fafafa);
    --segment-bg: #fcfcfc;
    --border-subtle: var(--border-primary, #e8e8e8);
    --text-primary: var(--text-primary, #1a1a1a);
    --text-secondary: var(--text-secondary, #6e6e6e);
    --accent: var(--accent-primary, #5856d6);
    --accent-hover: var(--accent-secondary, #4744c9);
  }

  /* Dark theme overrides */
  :root.dark-theme .split-editor {
    --diff-add: #4ade80;
    --diff-add-bg: transparent;
    --diff-del: #f87171;
    --diff-del-bg: transparent;
    --diff-equal: var(--text-secondary);
    --panel-bg: var(--bg-secondary, #111111);
    --segment-bg: #151515;
    --border-subtle: var(--border-primary, #262626);
    --text-primary: var(--text-primary, #f1f1f1);
    --text-secondary: var(--text-secondary, #a1a1a1);
    --accent: var(--accent-primary, #7c7aff);
    --accent-hover: var(--accent-secondary, #a78bfa);
  }

  /* Ultra-thin container */
  .split-editor {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--panel-bg);
    border-radius: 12px;
    contain: layout style;
    backface-visibility: hidden;
    transform: translateZ(0); /* GPU acceleration */
    overflow: hidden;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);
  }
  
  /* YAML Section Styles */
  .yaml-section {
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-subtle);
    margin: 0;
    transition: all 0.2s ease;
  }
  
  .yaml-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    background: transparent;
    border: none;
    color: var(--text-secondary);
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;
  }
  
  .yaml-toggle:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
  
  .yaml-toggle-icon {
    transition: transform 0.2s ease;
    flex-shrink: 0;
  }
  
  .yaml-toggle-icon.expanded {
    transform: rotate(90deg);
  }
  
  .yaml-label {
    font-weight: 500;
    color: var(--text-secondary);
  }
  
  .yaml-content {
    padding: 12px;
    background: var(--bg-primary);
    border-top: 1px solid var(--border-subtle);
    max-height: 200px;
    overflow-y: auto;
  }
  
  .yaml-content pre {
    margin: 0;
    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', monospace;
    font-size: 12px;
    line-height: 1.5;
    color: var(--text-tertiary);
    white-space: pre-wrap;
    word-break: break-word;
  }
  
  .split-editor.dragging {
    user-select: none;
  }


  /* Clean Panels */
  .panels-container {
    display: flex;
    flex: 1;
    overflow: hidden;
    position: relative;
    contain: layout style;
    will-change: auto;
  }
  
  .panel {
    display: flex;
    flex-direction: column;
    overflow: visible; /* Allow number badges to be visible */
    position: relative;
  }
  
  .panel-label {
    position: absolute;
    top: 12px;
    left: 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-secondary);
    opacity: 0.7;
    z-index: 10;
  }
  
  .diff-toggle {
    position: absolute;
    top: 10px;
    right: 16px;
    padding: 4px;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: var(--text-tertiary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
    z-index: 10;
    opacity: 0.6;
  }
  
  .diff-toggle:hover {
    background: var(--bg-hover, rgba(0, 0, 0, 0.04));
    color: var(--text-secondary);
    opacity: 0.9;
  }
  
  .diff-toggle:active {
    transform: scale(0.95);
  }
  
  
  .panel-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden; /* Hide horizontal overflow */
    padding: 2.5em 0.8em 2.2em 0.8em; /* Use em for scalable padding */
    background: var(--panel-bg);
    scrollbar-gutter: stable;
    /* Remove all containment to allow proper expansion */
    will-change: scroll-position;
  }
  
  /* Allow scrolling but hide scrollbar for original panel */
  .panel.original .panel-content {
    overflow-y: auto;  /* Allow vertical scrolling when content expands */
    overflow-x: hidden;
    scrollbar-width: none; /* Firefox */
  }
  
  /* Hide scrollbar for webkit browsers */
  .panel.original .panel-content::-webkit-scrollbar {
    display: none;
  }
  
  /* Only show scrollbar on suggested panel */
  .panel.suggested .panel-content::-webkit-scrollbar {
    width: 4px;
  }
  
  .panel.suggested .panel-content::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .panel.suggested .panel-content::-webkit-scrollbar-thumb {
    background: var(--border-subtle);
    border-radius: 2px;
  }
  
  /* Splitter */
  .splitter {
    width: 1px;
    background: var(--border-subtle);
    position: relative;
    cursor: col-resize;
    transition: all 0.2s;
  }
  
  .splitter:hover {
    background: var(--accent);
    width: 2px;
  }
  
  .splitter-handle {
    position: absolute;
    left: -4px;
    top: 50%;
    transform: translateY(-50%);
    width: 9px;
    height: 32px;
    opacity: 0;
    transition: opacity 0.2s;
  }
  
  .splitter:hover .splitter-handle {
    opacity: 1;
    background: var(--accent);
    border-radius: 4px;
  }

  /* Segment rows for alignment */
  .segment-row {
    display: flex;
    align-items: flex-start;
    margin: 0 4px;  /* Remove bottom margin - border provides separation */
    padding: 0.8em 0;  /* Reasonable padding */
    position: relative;
    overflow: hidden;  /* Prevent content overflow */
    will-change: contents;
    transition: all 0.15s ease;
    border-bottom: 1px solid var(--border-subtle);
    box-sizing: border-box;
  }
  
  .segment-row:first-child {
    padding-top: 0.3em;  /* Use em for scalable padding */
  }
  
  .segment-row:last-child {
    border-bottom: none;
    padding-bottom: 0.3em;  /* Use em for scalable padding */
  }
  
  .segment-row.hovered {
    opacity: 1;
  }
  
  .segment-row.hovered .segment-box,
  .segment-row.hovered .segment-box {
    background: rgba(88, 86, 214, 0.03);
    border: 1px solid rgba(88, 86, 214, 0.3);
    border-radius: 8px;
  }
  
  .segment-row.hovered .segment-number {
    background: var(--accent-primary);
    color: white;
  }
  
  /* Segment status styling */
  
  /* Applied state: Original becomes inactive, AI stays active (normal style) */
  .panel.original .segment-row.applied .segment-content {
    opacity: 0.5;
  }
  
  /* Skipped state: Original stays active, AI becomes inactive */
  .panel.original .segment-row.skipped .segment-content {
    opacity: 1;
  }
  
  .panel.suggested .segment-row.skipped .segment-content {
    opacity: 0.5;
  }
  
  /* Segment number badge - left aligned style */
  .segment-number {
    position: absolute;
    top: 0.2em;  /* Aligned with text baseline using em */
    left: 0.25em;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.2em;
    height: 1.2em;
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    border-radius: 50%;
    font-size: 0.7em;  /* Scale with parent font size */
    font-weight: 600;
    flex-shrink: 0;
    z-index: 1;
    transition: all 0.15s ease;
  }
  
  
  .segment-box {
    position: relative;
    padding: 0.6em 0.5em 0.6em 1.8em; /* Slightly more vertical padding */
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    width: 100%;
    min-height: 4em;  /* Use em for scalable height based on font size */
    display: flex;
    flex-direction: column;
    justify-content: flex-start;  /* Change to flex-start for better alignment */
    transition: all 0.2s ease;
    overflow: hidden;  /* Prevent content overflow */
    box-sizing: border-box;
    will-change: contents;
    transform: translateZ(0); /* Force GPU acceleration */
  }
  
  .segment-box.has-changes {
    background: transparent;
  }
  
  .segment-content {
    flex: 1 1 auto;
    overflow: hidden;  /* Prevent content overflow */
    min-height: 1.5em;  /* Use em for scalable height based on font size */
    word-wrap: break-word;
    word-break: keep-all;
  }
  
  .text-content {
    font-size: var(--editor-font-size);
    line-height: var(--editor-line-height);
    color: var(--text-primary);
    white-space: pre-wrap;
    word-break: keep-all;
  }
  
  /* Clickable text content for editing */
  .text-content.clickable,
  .diff-content.clickable {
    cursor: text;
    transition: background-color 0.1s ease;
  }
  
  /* Reset button styles for text-content buttons - match div.text-content exactly */
  button.text-content,
  button.diff-content {
    display: block;
    width: 100%;
    text-align: left;
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    font-family: inherit;
    font-size: var(--editor-font-size);
    line-height: var(--editor-line-height);
    color: var(--text-primary);
    font-weight: normal;
    white-space: pre-wrap;
    word-break: keep-all;
    cursor: pointer;
  }

  .text-content.clickable:hover,
  .diff-content.clickable:hover {
    background: rgba(88, 86, 214, 0.05);
    border-radius: 4px;
  }
  
  /* Editable text content - maintain exact same layout as non-editable */
  .text-content.editable {
    outline: none;
    cursor: text;
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    font-family: inherit;
    font-size: var(--editor-font-size);
    line-height: var(--editor-line-height);
    color: inherit;
    white-space: pre-wrap;
    word-break: keep-all;
    min-height: inherit;  /* Match parent's min-height */
    caret-color: var(--accent);
    display: block;
    width: 100%;
    font-weight: normal;
    letter-spacing: normal;
    /* Preserve exact text rendering */
    -webkit-font-smoothing: inherit;
    -moz-osx-font-smoothing: inherit;
  }
  
  /* Preserve formatting in contenteditable */
  .text-content.editable[contenteditable="true"] {
    /* Reset any browser default contenteditable styles */
    -webkit-user-modify: read-write;
    overflow-wrap: break-word;
  }
  
  /* Ensure consistent rendering of line breaks in editable mode */
  /* Using :global() because br tags are dynamically inserted by contenteditable */
  .text-content.editable :global(br) {
    display: block;
    content: "";
    margin: 0;
    padding: 0;
  }
  
  /* Very subtle highlight when focused */
  .text-content.editable:focus {
    background: rgba(88, 86, 214, 0.02);
    border-radius: 2px;
  }
  
  /* Dark theme adjustments for editable */
  :global(.dark-theme) .text-content.editable:focus {
    background: rgba(124, 122, 255, 0.05);
  }
  
  :global(.dark-theme) .text-content.editable {
    caret-color: var(--accent);
  }
  
  .diff-content {
    font-size: var(--editor-font-size);
    line-height: var(--editor-line-height);
    word-break: keep-all;
  }
  
  /* Status messages */
  /* Status messages - currently unused
  .status-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: var(--editor-font-size);
    font-weight: 500;
    padding: 12px;
    border-radius: 6px;
  }
  
  .status-message svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  
  .status-message.applied {
    color: var(--success);
    background: rgba(34, 197, 94, 0.1);
  }
  
  .status-message.applied svg {
    stroke-width: 2;
  }
  
  .status-message.skipped {
    color: var(--text-tertiary);
    background: rgba(0, 0, 0, 0.05);
  }
  
  .status-message.skipped svg {
    stroke-width: 1.5;
  }
  */
  
  /* Diff Styles - Ultra minimal for Korean text */
  :global(.diff-panel-content) {
    font-size: var(--editor-font-size);
    line-height: var(--editor-line-height);
    word-break: keep-all;
  }
  
  :global(.diff-panel-content br) {
    display: block;
    content: "";
    margin-top: 0.5em;
  }
  
  :global(.diff-equal) {
    color: var(--text-primary);
  }
  
  :global(.diff-delete) {
    color: var(--diff-del);
    text-decoration: line-through;
    text-decoration-thickness: 1.5px;
    text-decoration-color: var(--diff-del);
  }
  
  :global(.diff-insert) {
    color: var(--diff-add);
    font-weight: 500;
  }
  
  :global(.diff-delete-placeholder),
  :global(.diff-insert-placeholder) {
    display: none;
  }


  /* Skeleton Loader */
  .skeleton-text {
    padding: 8px 0;
    position: relative;
  }
  
  .skeleton-line {
    height: 16px;
    background: linear-gradient(90deg, var(--border-subtle) 25%, transparent 50%, var(--border-subtle) 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 8px;
  }
  
  .skeleton-line.short {
    width: 60%;
  }
  
  .processing-label {
    position: absolute;
    bottom: -4px;
    left: 0;
    font-size: 11px;
    color: var(--accent);
    font-weight: 500;
    opacity: 0.8;
    animation: pulse 2s infinite;
  }
  
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  
  .streaming-indicator {
    position: absolute;
    right: 0;
    top: 0;
    animation: blink 1s infinite;
    color: var(--primary-color);
    font-weight: bold;
    margin-left: 4px;
  }
  
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
  
  .text-content.streaming {
    position: relative;
    padding-right: 20px;
    display: inline-block;
    min-width: 100%;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.8; }
    50% { opacity: 0.4; }
  }
  
  /* Processing state */
  .segment-box.is-processing {
    opacity: 0.7;
    pointer-events: none;
  }
  
  .segment-box.is-processing .segment-content {
    cursor: not-allowed;
  }


  /* Dark mode segment number and status */
  :root.dark-theme .segment-number {
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-primary);
  }
  
  :root.dark-theme .segment-row.hovered .segment-number {
    background: var(--accent-primary);
    color: white;
  }
  
  /* :root.dark-theme .status-message.skipped {
    background: rgba(255, 255, 255, 0.05);
  } */
  
  
  /* Dark theme border */
  :root.dark-theme .segment-row {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }
  
  /* Dark theme hover */
  :root.dark-theme .segment-row.hovered .segment-box {
    background: rgba(124, 122, 255, 0.05);
    border: 1px solid rgba(124, 122, 255, 0.3);
  }
  
  /* Panel footer - ultra thin char count */
  .panel-footer {
    position: absolute;
    bottom: 8px;
    right: 12px;
    font-size: 11px;
    z-index: 10;
    opacity: 0.6;
    transition: opacity 0.15s ease;
  }
  
  .panel:hover .panel-footer {
    opacity: 1;
  }
  
  .panel-footer .stat-value {
    font-weight: 600;
    color: var(--text-secondary, #666);
    font-size: 11px;
  }

  .panel-footer .korean-count {
    font-weight: 400;
    opacity: 0.8;
    margin-left: 4px;
    font-size: 10px;
  }
  
  /* Dark theme panel footer */
  :root.dark-theme .panel-footer .stat-value {
    color: var(--text-secondary, #a1a1a1);
  }

  :root.dark-theme .panel-footer .korean-count {
    opacity: 0.7;
  }
  
  /* Responsive */
  @media (max-width: 768px) {
    .panels-container {
      flex-direction: column;
    }
    
    .splitter {
      width: 100%;
      height: 1px;
      cursor: row-resize;
    }
    
    .splitter:hover {
      height: 2px;
    }
    
    .panel {
      width: 100% !important;
      min-height: 200px;
    }
  }
  
  /* Split Bulk Prompt Input Styles */
  .split-bulk-prompt-bar {
    margin: 12px;
    margin-top: 8px;
    background: #f9f9fb;
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    z-index: 100;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
  
  .split-chat-container {
    position: relative;
    padding: 8px 8px;
  }
  
  /* Main textarea */
  .split-prompt-input {
    width: 100%;
    padding: 6px 12px;
    padding-bottom: 28px; /* Space for bottom controls */
    font-size: 13px;
    line-height: 1.4;
    border: none;
    background: transparent;
    outline: none;
    resize: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    min-height: 32px;
    max-height: 100px;
    overflow-y: auto;
    color: var(--text-primary);
  }
  
  .split-prompt-input::placeholder {
    color: var(--text-secondary);
    font-size: 13px;
  }
  
  /* Processing state */
  .split-processing-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    min-height: 32px;
  }
  
  .split-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid var(--border-subtle);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: split-spin 0.8s linear infinite;
  }
  
  @keyframes split-spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  .split-processing-text {
    font-size: 13px;
    color: var(--text-secondary);
  }
  
  /* Bottom controls */
  .split-bottom-controls {
    position: absolute;
    bottom: 8px;
    left: 12px;
    right: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }
  
  /* Model selector */
  .split-model-selector {
    position: relative;
  }
  
  .split-model-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: white;
    border: 1px solid var(--border-subtle);
    border-radius: 5px;
    font-size: 12px;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.15s;
  }
  
  .split-model-btn:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #d1d5db;
  }
  
  .split-model-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .split-model-name {
    font-weight: 500;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .split-chevron-icon {
    transition: transform 0.2s;
  }
  
  .split-chevron-icon.rotated {
    transform: rotate(180deg);
  }
  
  /* Model dropdown */
  .split-model-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 4px;
    background: white;
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 9999;
    min-width: 220px;
    max-width: 300px;
  }
  
  .split-model-list {
    max-height: 280px;
    overflow-y: auto;
    padding: 4px;
  }
  
  .split-model-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 12px;
    background: transparent;
    border: none;
    border-radius: 5px;
    font-size: 13px;
    color: var(--text-primary);
    cursor: pointer;
    transition: background 0.15s;
    text-align: left;
  }
  
  .split-model-item:hover {
    background: #f9fafb;
  }
  
  .split-model-item.selected {
    background: #eff6ff;
    color: #2563eb;
  }
  
  .split-item-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  
  .split-item-name {
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .split-recommended-badge {
    background: #fef3c7;
    color: #92400e;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
  }
  
  .split-check-icon {
    flex-shrink: 0;
  }
  
  /* Right controls */
  .split-right-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .split-char-count {
    font-size: 11px;
    color: var(--text-secondary);
    font-family: 'SF Mono', Monaco, monospace;
    opacity: 0.7;
  }
  
  /* Send button */
  .split-send-btn {
    width: 28px;
    height: 28px;
    border-radius: 5px;
    background: var(--accent);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }
  
  .split-send-btn:hover:not(:disabled) {
    background: var(--accent-hover);
    transform: scale(1.05);
  }
  
  .split-send-btn:disabled {
    background: var(--border-subtle);
    cursor: not-allowed;
  }
  
  .split-send-btn svg {
    width: 14px;
    height: 14px;
    color: white;
  }
  
  .split-send-btn:disabled svg {
    color: var(--text-secondary);
  }
  
  /* Dark theme for split prompt */
  :root.dark-theme .split-bulk-prompt-bar {
    background: #1f2937;
    border-color: var(--border-subtle);
  }
  
  :root.dark-theme .split-model-btn {
    background: #374151;
    border-color: var(--border-subtle);
  }
  
  :root.dark-theme .split-model-btn:hover:not(:disabled) {
    background: #4b5563;
  }
  
  :root.dark-theme .split-model-dropdown {
    background: #374151;
    border-color: var(--border-subtle);
  }
  
  :root.dark-theme .split-model-item:hover {
    background: #4b5563;
  }
  
  :root.dark-theme .split-model-item.selected {
    background: #4c1d95;
    color: #a78bfa;
  }
  
  :root.dark-theme .split-recommended-badge {
    background: rgba(254, 243, 199, 0.15);
    color: #fbbf24;
  }
  
  
  /* Dark theme for split bulk prompt */
  :root.dark-theme .split-prompt-input {
    color: #f3f4f6;
    background: transparent;
  }
  
  :root.dark-theme .split-prompt-input::placeholder {
    color: #6b7280;
  }
  
  :root.dark-theme .split-prompt-input::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
  }
  
  :root.dark-theme .split-char-count {
    color: #6b7280;
  }
  
  :root.dark-theme .split-send-btn {
    background: #7c7aff;
  }
  
  :root.dark-theme .split-send-btn:hover:not(:disabled) {
    background: #a78bfa;
  }
  
  :root.dark-theme .split-send-btn:disabled {
    background: rgba(255, 255, 255, 0.1);
  }
</style>